/**
 * Kickard PHASE 2.2 — Online match screen (UI parity with local GameScreen).
 *
 * Pure renderer + dispatcher for the server-authoritative state. Visually
 * mirrors `GameScreen.jsx` (pitch background, top HUD with scores + deck +
 * half label, central action hub on the midline, bottom turn/discard
 * status, reveal overlays) so online and local feel like the same game.
 *
 * Online specifics:
 *   • Viewer is the local `playerId` (may be 'p2'), passed to GameBoard so
 *     the viewer always sits at the bottom of the board.
 *   • DRAW / PLACE / ATTACK / JOKER_* actions go over the WebSocket; the
 *     UI never mutates state locally. The displayed deck remaining count
 *     and half indicator come from the masked server view (`deckRemaining`,
 *     `half`).
 *   • No bot driver, no kickoff overlay, no penalty drive — those belong to
 *     local mode. Online keeps the visual feel without local engine code.
 *   • Hidden-info masking is enforced server-side; this component does not
 *     attempt to peek at opponent state.
 */
import React, { useEffect, useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import GameBoard from '@/components/GameBoard';
import PlayingCard from '@/components/PlayingCard';
import JokerSequence from '@/components/JokerSequence';
import ShotSequence from '@/components/ShotSequence';
import DoubleChanceSequence from '@/components/DoubleChanceSequence';
import HalftimeOverlay from '@/components/HalftimeOverlay';
import MatchSummaryView from '@/screens/MatchSummaryView';
import PenaltyView from '@/screens/PenaltyView';
import Scoreboard from '@/components/Scoreboard';
import PitchBackground from '@/components/PitchBackground';
import MidfieldDrawBall from '@/components/MidfieldDrawBall';
import MatchStatusBar from '@/components/MatchStatusBar';
import H2HCard from '@/components/H2HCard';
import { useHeadToHead } from '@/hooks/useHeadToHead';
import { useDeferredScores } from '@/hooks/useDeferredScores';
import { TID } from '@/constants/testIds';
import { sounds } from '@/audio/sounds';
import { OnlineSocket } from '@/net/socket';
import { saveOnlineSession, clearOnlineSession } from '@/net/onlineSession';
import { hasAnyValidAction } from '@/engine/rules';
import { useScreenWakeLock } from '@/hooks/useScreenWakeLock';
import { storage } from '@/lib/storage';
import { loadGuest } from '@/lib/guestIdentity';

const TURN_PASS_HOLD_MS = 800;
const NO_MOVE_VISIBLE_MS = 1500;
const RECONNECT_BUDGET_MS = 30_000;
// Final-reveal hold for penalty shootout — matches GameScreen so the
// last shot stays visible BEFORE the match-summary replaces it.
const PENALTY_REVEAL_HOLD_MS = 2400; // Phase 3.8.2 — deciding-shot hold

export default function OnlineGameScreen({ session, onLeave }) {
  // session = { roomCode, playerId, token, name }
  const { roomCode, playerId, token } = session;
  const other = playerId === 'p1' ? 'p2' : 'p1';

  const [connected, setConnected] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [opponentName, setOpponentName] = useState(null);
  const [error, setError] = useState(null);
  const [state, setState] = useState(null);
  const [serverTick, setServerTick] = useState(0);
  // Phase 3.11 — Friend Match rematch coordination.
  const [matchType, setMatchType] = useState(session?.matchType || null);
  const [rematch, setRematch] = useState({
    status: 'none',
    isRequester: false,
    opponentIsRequester: false,
    expiresAt: null,
    requestedByName: null,
    previousMatchId: null,
    // PHASE 4.3.2B — chain metadata carried from server broadcasts.
    // `rematchIndex` counts matches ALREADY completed in this chain
    // (0 = original). Frontend caps the "Rematch" button on Quick when
    // the next index would exceed the mode's `directRematchMaxIndex`.
    rematchChainId: null,
    rematchIndex: 0,
  });

  // Phase 3.8 — keep screen awake during active online gameplay
  // (Friend Match / Quick Match / penalties / waiting-for-opponent during
  // an active match). Released on match-end / exit / unmount. Honors the
  // user's "Keep screen awake during matches" setting (default ON).
  const wakeLockEnabled =
    storage.getSettings().keepScreenAwake !== false &&
    state !== null &&
    state.phase !== 'match-end' &&
    state.phase !== 'finished';
  useScreenWakeLock(wakeLockEnabled);


  // Reconnect bookkeeping (purely UI state — the socket layer drives it).
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectInfo, setReconnectInfo] = useState({ attempt: 0, elapsedMs: 0, totalMs: RECONNECT_BUDGET_MS });
  const [reconnectFailed, setReconnectFailed] = useState(false);

  // Server-authoritative timer state (Phase 2.4).
  const [timer, setTimer] = useState(null);
  const [serverClockSkew, setServerClockSkew] = useState(0); // serverNow - clientNow
  // Re-render every 250 ms so countdowns tick smoothly even without
  // server pushes.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setClockTick((n) => n + 1), 250);
    return () => clearInterval(id);
  }, []);

  // Phase 3.5 — Head-to-head rivalry (online matches only).
  // P0 hotfix — only fetch once the opponent has actually connected.
  // Before that, the backend returns a zero-payload (no opponent
  // guestId yet) which used to render as a misleading "First meeting".
  const { h2h, refetch: refetchH2h } = useHeadToHead(session, { enabled: opponentConnected });

  // P0 hotfix (Phase 3.5.4) — once the match has concluded, re-fetch
  // H2H so the Match Summary reflects the just-finished match (e.g.,
  // pre-match 2-2 → post-match 3-2 if I won). The server now writes
  // match_history BEFORE broadcasting the terminal state, so by the
  // time we observe the terminal phase, the row is committed in Mongo
  // and the re-fetch is guaranteed to include the just-finished match.
  // A small retry with a single backoff covers any cross-pod write
  // visibility delay; until the updated payload arrives we keep
  // showing the pre-match H2H plus an "Updating rivalry record…" chip.
  const [h2hSummary, setH2hSummary] = useState(null);
  const [h2hSummaryUpdating, setH2hSummaryUpdating] = useState(false);
  const terminalPhase = state?.phase === 'match-end' || state?.phase === 'finished';
  useEffect(() => {
    if (!terminalPhase) return;
    if (h2hSummary) return; // already refreshed for this match
    let cancelled = false;
    setH2hSummaryUpdating(true);
    (async () => {
      // Small grace for any cross-pod replication.
      const first = await refetchH2h();
      if (cancelled) return;
      if (first) {
        setH2hSummary(first);
        setH2hSummaryUpdating(false);
        return;
      }
      // Retry once after 600ms in case the history row hasn't propagated.
      await new Promise((res) => setTimeout(res, 600));
      if (cancelled) return;
      const second = await refetchH2h();
      if (cancelled) return;
      setH2hSummary(second || null);
      setH2hSummaryUpdating(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [terminalPhase, h2hSummary, refetchH2h]);

  // Reveal sequences (same shape as local GameScreen).
  const [jokerSeq, setJokerSeq] = useState(null);
  const [shotSeq, setShotSeq] = useState(null);
  const [dcSeq, setDcSeq] = useState(null);
  /**
   * Phase 3.8.2.1 — Inline no-valid-move discard flash (see GameScreen.jsx).
   * Reverted from the centered DiscardReveal modal: card now flashes for
   * ~900 ms in the drawn-card slot instead.
   */
  const [discardFlash, setDiscardFlash] = useState(null);
  // Auto-clear inline discard flash after 900 ms (must live before any
  // early return so hook order stays stable).
  useEffect(() => {
    if (!discardFlash) return undefined;
    const t = setTimeout(() => setDiscardFlash(null), 900);
    return () => clearTimeout(t);
  }, [discardFlash]);
  const [holdUntil, setHoldUntil] = useState(0);
  // Brief reveal of the opponent's attacking card on a NORMAL attack
  // (non-GK shot, non-joker, non-DC). In local mode the bot's drawn
  // card was always visible, so the player naturally saw the attacker
  // — in online we mask it for privacy, then briefly unmask it during
  // the attack moment so the receiver can SEE which card hit them.
  // {attacker: Card, target: Card, expiresAt: number} | null
  const [attackReveal, setAttackReveal] = useState(null);
  // Penalty shootout — hold the last-shot reveal frame visible for
  // PENALTY_REVEAL_HOLD_MS BEFORE the match-summary screen replaces it.
  const [penaltyRevealUntil, setPenaltyRevealUntil] = useState(0);
  /**
   * Phase 3.8.2.3 — Robust terminal penalty reveal gate.
   *
   * The 3.8.2.2 `terminalGraceUntil` (900 ms) proved insufficient on real
   * devices: the PENALTY_REVEAL event could arrive AFTER the 900 ms window
   * elapsed (network jitter / cross-pod delay), causing the summary to
   * render before the reveal.
   *
   * This new gate is EVENT-DRIVEN:
   *   1. When the incoming STATE has a terminal phase (`finished`/`match-end`)
   *      AND `state.penalty.shots.length` grew this update AND
   *      `penaltyRevealUntil` has not yet been set for the new shot, we
   *      ARM the gate (`pendingTerminalPenaltyReveal = true`).
   *   2. The gate is DISARMED only when the corresponding PENALTY_REVEAL
   *      event actually arrives (which sets `penaltyRevealUntil`). We wait
   *      for the reveal hold to expire and THEN show the summary.
   *   3. As a hard fallback (in case the server never delivered the event
   *      because of a bug), a 6 s timer clears the gate. This is well
   *      beyond the 2400 ms reveal hold + reasonable network jitter, so
   *      the user is never permanently stuck on the board.
   *
   * The old 900 ms `terminalGraceUntil` remains as a secondary belt because
   * it still helps the (rare) case where state + events arrive together
   * and React batches them — but the primary gate is the pending flag.
   */
  const [terminalGraceUntil, setTerminalGraceUntil] = useState(0);
  const [pendingTerminalPenaltyReveal, setPendingTerminalPenaltyReveal] = useState(false);
  const terminalGateRef = useRef({
    lastShotCount: 0,
    fallbackTimeout: null,
  });
  // Fallback release: never keep the summary hidden longer than 6 s.
  const armTerminalPenaltyGate = useCallback(() => {
    setPendingTerminalPenaltyReveal(true);
    if (terminalGateRef.current.fallbackTimeout) {
      clearTimeout(terminalGateRef.current.fallbackTimeout);
    }
    terminalGateRef.current.fallbackTimeout = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('[terminalPenaltyGate] fallback release after 6s — PENALTY_REVEAL never arrived');
      setPendingTerminalPenaltyReveal(false);
    }, 6000);
  }, []);
  const disarmTerminalPenaltyGate = useCallback(() => {
    setPendingTerminalPenaltyReveal(false);
    if (terminalGateRef.current.fallbackTimeout) {
      clearTimeout(terminalGateRef.current.fallbackTimeout);
      terminalGateRef.current.fallbackTimeout = null;
    }
  }, []);
  useEffect(() => () => {
    if (terminalGateRef.current.fallbackTimeout) {
      clearTimeout(terminalGateRef.current.fallbackTimeout);
    }
  }, []);

  // P0 hotfix — pre-match rivalry reveal. Once the opponent connects
  // AND the H2H payload has landed, we hold the H2H card visible for
  // PRE_MATCH_REVEAL_MS so the player can actually read the rivalry
  // record. The overlay is non-blocking (pointer-events-none) so the
  // server-driven timer keeps ticking and the first DRAW is fully
  // clickable underneath.
  const PRE_MATCH_REVEAL_MS = 3500;
  const [preMatchRevealUntil, setPreMatchRevealUntil] = useState(0);
  useEffect(() => {
    if (opponentConnected && h2h && preMatchRevealUntil === 0) {
      setPreMatchRevealUntil(Date.now() + PRE_MATCH_REVEAL_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opponentConnected, h2h]);
  const preMatchRevealActive = preMatchRevealUntil > 0 && Date.now() < preMatchRevealUntil;

  // P0 anti-spoiler — display the previous score until the reveal
  // sequence completes. Backend scoring logic is untouched; this is
  // purely a display-only lag so the scoreboard cannot leak the
  // outcome before the cards animate. Hook MUST be called before any
  // early return to keep hook order stable across renders.
  const attackRevealActiveForGuard = attackReveal !== null && Date.now() < attackReveal.expiresAt;
  const pendingRevealForGuard = jokerSeq !== null || shotSeq !== null || dcSeq !== null || attackRevealActiveForGuard;
  const displayScores = useDeferredScores(
    state?.players?.p1?.score ?? 0,
    state?.players?.p2?.score ?? 0,
    pendingRevealForGuard || Date.now() < holdUntil
  );

  // Dedupe events by tick — server may resend the same state msg when only
  // the timer subdocument changes (no-valid-move flag, time-bank start).
  // Without this guard, every timer broadcast would replay Joker / Shot /
  // DC animations and sounds.
  const lastEventsTickRef = useRef(-1);

  const socketRef = useRef(null);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[OnlineGame] mount', { playerId, roomCode });

    const sock = new OnlineSocket(roomCode, token, {
      onJoined: (m) => {
        setConnected(true);
        setError(null);
        setOpponentConnected(!!m.opponentConnected);
        setOpponentName(m.opponentName);
        if (m.matchType) setMatchType(m.matchType);
        // Persist so a deliberate reload within 60 s reattaches as the
        // same player.
        saveOnlineSession({
          roomCode,
          playerId: m.playerId || playerId,
          token,
          name: session.name,
          opponentName: m.opponentName,
          matchType: m.matchType,
        });
      },
      onStateMessage: (msg) => {
        // PHASE 3.13.2 hotfix — defensive dismiss. Any authoritative
        // server state message means the socket is live and re-attached to
        // this room. If the UI is still showing the "Reconnecting…" modal
        // (e.g. the low-level onReconnected() callback was missed for any
        // reason), clear it here so the player is never left stuck behind
        // an overlay while the live match ticks along beneath.
        setReconnecting((r) => (r ? false : r));
        setReconnectFailed((f) => (f ? false : f));
        // Phase 3.8.2.2 — Terminal grace (secondary belt).
        // Phase 3.8.2.3 — Primary event-driven gate: if incoming state has
        // just transitioned to terminal AND the penalty shots array grew
        // (a shot was just applied), arm the pending-reveal flag. It will
        // only disarm when the PENALTY_REVEAL event arrives in handleEvents
        // (which sets penaltyRevealUntil) OR when the 6 s fallback fires.
        const newPhase = msg.state?.phase;
        const isTerminal = newPhase === 'finished' || newPhase === 'match-end';
        const shotsLen = msg.state?.penalty?.shots?.length || 0;
        const shotsGrew = shotsLen > terminalGateRef.current.lastShotCount;
        terminalGateRef.current.lastShotCount = shotsLen;
        if (isTerminal && shotsGrew) {
          armTerminalPenaltyGate();
        }
        if (isTerminal) {
          setTerminalGraceUntil((t) => (t > 0 ? t : Date.now() + 900));
        }
        setState(msg.state);
        setServerTick(msg.tick || 0);
        if (msg.timer) setTimer(msg.timer);
        if (typeof msg.serverNow === 'number') {
          setServerClockSkew(msg.serverNow - Date.now());
        }
        // eslint-disable-next-line no-console
        console.log('[OnlineGame] state', {
          tick: msg.tick,
          hash: msg.stateHash,
          viewer: msg.state?.viewerId,
          phase: msg.state?.phase,
          cur: msg.state?.currentPlayer,
          timerPhase: msg.timer?.phase,
          strikes: msg.timer?.strikes,
        });
        if (msg.state?.players?.[other]) setOpponentConnected(true);
      },
      onEvents: (events, tick) => {
        // Dedupe by tick — never replay sounds/animations on a re-broadcast.
        if (typeof tick === 'number' && tick <= lastEventsTickRef.current) {
          // eslint-disable-next-line no-console
          console.log('[OnlineGame] events SKIPPED (dup tick)', { tick, last: lastEventsTickRef.current });
          return;
        }
        if (typeof tick === 'number') lastEventsTickRef.current = tick;
        // eslint-disable-next-line no-console
        console.log('[OnlineGame] events', { tick, types: events.map((e) => e.type) });
        handleEvents(events);
      },
      onRematchMessage: (msg) => {
        // eslint-disable-next-line no-console
        console.log('[OnlineGame] rematch msg', msg);
        handleRematchMessage(msg);
      },
      onError: (m) => {
        setError(m);
        // A terminal error during reconnect means the budget ran out OR
        // the room is gone for good. Show the "give up" UI.
        if (reconnecting) setReconnectFailed(true);
      },
      onOpponentDisconnected: () => setOpponentConnected(false),
      onOpponentReconnected: () => setOpponentConnected(true),
      onReconnecting: (info) => {
        setReconnecting(true);
        setReconnectInfo(info);
      },
      onReconnected: () => {
        setReconnecting(false);
        setReconnectFailed(false);
        setError(null);
      },
      onClose: () => {
        setConnected(false);
        // We've given up reconnecting; the saved session can no longer be
        // used to reattach. Clear it so a future visit lands on the lobby.
        clearOnlineSession();
      },
    });
    socketRef.current = sock;
    sock.connect();

    // Phase 3.8.2 — iPhone reconnect fix. When the app returns to the
    // foreground (visibilitychange visible OR pageshow), sanity-check the
    // socket. iOS silently kills WebSockets while suspended; ws.readyState
    // may still say OPEN. `sock.resume()` resets the reconnect budget and
    // force-reconnects if the socket is stale or dead.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        try { socketRef.current?.resume(); } catch (e) { /* ignore */ }
      }
    };
    const onPageShow = () => {
      try { socketRef.current?.resume(); } catch (e) { /* ignore */ }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onPageShow);
      sock.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomCode, token]);

  // Map server events to the same reveal sequences as local GameScreen.
  function handleEvents(events) {
    if (!events || events.length === 0) return;

    for (const ev of events) {
      if (ev.type === 'CARD_DRAWN') sounds.draw();
      if (ev.type === 'CARD_PLACED') sounds.place();
      if (ev.type === 'ATTACK_RESULT' && ev.outcome === 'win') sounds.attackWin();
    }

    // Opponent normal attack reveal — mirrors local mode where the bot's
    // drawn card was visible. Briefly unmask the opponent's attacker.
    // Skipped for our OWN attacks (we already saw the card face-up) and
    // for GK shots / jokers / DC (those have their own ShotSequence /
    // JokerSequence / DoubleChanceSequence reveals).
    const attackEv = events.find((e) => e.type === 'ATTACK_RESULT');
    if (attackEv && attackEv.from && attackEv.from !== playerId && attackEv.attacker) {
      setAttackReveal({
        attacker: attackEv.attacker,
        target: attackEv.defender,
        expiresAt: Date.now() + 900,
      });
      // Hold the board so the next event/state can't preempt the reveal.
      setHoldUntil((h) => Math.max(h, Date.now() + 900));
    }

    const jokerEv = events.find((e) => e.type === 'JOKER');
    const goalEv = events.find((e) => e.type === 'GOAL');
    const saveEv = events.find((e) => e.type === 'SAVE');
    const turnPassEv = events.find((e) => e.type === 'TURN_PASSED');
    const dcAtkEv = events.find((e) => e.type === 'JOKER_DOUBLE_ATTACK_RESULT');
    const dcDefEv = events.find((e) => e.type === 'JOKER_DC_DEFENSE_RESULT');

    if (jokerEv) {
      const outcome = goalEv ? 'goal' : saveEv ? 'save' : 'goal';
      const gk = goalEv?.gk ?? saveEv?.gk ?? null;
      // Phase 3.8.2 — Joker SHOOT vs DC GK reveal fix (see GameScreen.jsx).
      const outcomeEv = goalEv || saveEv;
      if (outcomeEv && outcomeEv.dcDraws && outcomeEv.dcDraws.length > 0 && outcomeEv.gk) {
        const isGoal = outcomeEv.type === 'GOAL';
        const meIsShooter = (isGoal ? outcomeEv.scorer : (outcomeEv.defender === playerId ? other : playerId)) === playerId;
        const positive = meIsShooter ? isGoal : !isGoal;
        setDcSeq({
          mode: 'defense',
          primaryCard: outcomeEv.shooter,
          targetCard: outcomeEv.gk,
          draws: outcomeEv.dcDraws,
          resultPositive: positive,
          resultLabel: isGoal ? 'GOAL!' : 'SAVE!',
        });
      } else {
        setJokerSeq({
          jokerCard: jokerEv.jokerCard,
          followUp: jokerEv.followUp,
          gk,
          outcome,
        });
      }
    } else if (goalEv || saveEv) {
      const ev = goalEv || saveEv;
      if (ev.dcDraws && ev.dcDraws.length > 0 && ev.gk) {
        const isGoal = ev.type === 'GOAL';
        const meIsShooter = (isGoal ? ev.scorer : (ev.defender === playerId ? other : playerId)) === playerId;
        const positive = meIsShooter ? isGoal : !isGoal;
        setDcSeq({
          mode: 'defense',
          primaryCard: ev.shooter,
          targetCard: ev.gk,
          draws: ev.dcDraws,
          resultPositive: positive,
          resultLabel: isGoal ? 'GOAL!' : 'SAVE!',
        });
      } else {
        setShotSeq({
          shooter: ev.shooter,
          gk: ev.gk,
          outcome: goalEv ? 'goal' : 'save',
        });
      }
    }

    if (dcAtkEv) {
      setDcSeq({
        mode: 'attack',
        primaryCard: dcAtkEv.jokerCard,
        targetCard: dcAtkEv.targetCard,
        draws: dcAtkEv.draws,
        attackerDraws: dcAtkEv.attackerDraws,
        defenderDraws: dcAtkEv.defenderDraws,
        resultPositive: dcAtkEv.success,
        resultLabel: dcAtkEv.success ? 'TARGET REMOVED' : 'TARGET SURVIVED',
      });
    } else if (dcDefEv) {
      const meIsDefender = dcDefEv.defenderId === playerId;
      const positive = meIsDefender ? dcDefEv.blocked : !dcDefEv.blocked;
      setDcSeq({
        mode: 'defense',
        primaryCard: dcDefEv.attacker,
        targetCard: dcDefEv.dcCard,
        draws: dcDefEv.draws,
        resultPositive: positive,
        resultLabel: dcDefEv.blocked ? 'BLOCKED' : 'DC REMOVED',
      });
    }

    if (turnPassEv) {
      setHoldUntil((h) => Math.max(h, Date.now() + TURN_PASS_HOLD_MS));
    }

    // Phase 3.8.2.1 — Inline no-valid-move discard flash. Renders the card
    // in the normal drawn-card slot for ~900 ms instead of the previous
    // centered modal (visually too heavy). Same source event, different UI.
    const drawDiscardedEv = events.find((e) => e.type === 'DRAW_DISCARDED');
    if (drawDiscardedEv && drawDiscardedEv.card) {
      setDiscardFlash({
        id: `dsc-${state?.tick || 0}-${Date.now()}`,
        card: drawDiscardedEv.card,
        byMe: drawDiscardedEv.player === playerId,
      });
    }

    // Penalty reveal — keep the last-shot frame visible before the
    // shooter swap or before the match-summary screen replaces it.
    // Phase 3.8.2.3: also disarm the terminal-penalty gate — the reveal
    // event has arrived, so the summary can now safely appear AFTER the
    // reveal hold elapses.
    const penaltyRevealEv = events.find((e) => e.type === 'PENALTY_REVEAL');
    if (penaltyRevealEv) {
      setPenaltyRevealUntil((p) => Math.max(p, Date.now() + PENALTY_REVEAL_HOLD_MS));
      // The gate is released only once the reveal hold expires (below), but
      // we clear the fallback timer here so the fallback doesn't fire
      // AFTER the reveal completes normally.
      if (terminalGateRef.current.fallbackTimeout) {
        clearTimeout(terminalGateRef.current.fallbackTimeout);
        terminalGateRef.current.fallbackTimeout = null;
      }
    }
  }

  // Leave-the-room helper. Always clears the persisted session so a future
  // /online visit lands on a clean lobby (no "stale ghost match").
  const handleLeave = () => {
    clearOnlineSession();
    onLeave?.();
  };

  // ========================================================================
  //  Phase 3.11 — Friend Match Rematch coordination
  //  Phase 3.11.1 hotfix — every action is scoped by `previousMatchId`
  //  (the client's view of the just-completed match's engineMatchId).
  //  Stale events for a different match are ignored so we can never see
  //  a phantom "Waiting for X" on the next match's summary.
  // ========================================================================

  // Track the engine matchId currently on screen. When it changes, we
  // know a fresh match has begun — reset any lingering rematch UI AND
  // any lingering terminal-reveal gate state so a match-2 (or later)
  // ending on penalties gets the full reveal window.
  const currentEngineMatchId = state?.matchId || null;
  const prevEngineMatchIdRef = useRef(currentEngineMatchId);
  useEffect(() => {
    if (currentEngineMatchId && prevEngineMatchIdRef.current &&
        currentEngineMatchId !== prevEngineMatchIdRef.current) {
      // New match — reset local rematch UI to a truly idle state.
      // PHASE 4.3.2B — but preserve chain metadata so the cap check
      // for Quick correctly hides Rematch on the 3rd match of the
      // chain. The chain metadata was already set by the preceding
      // `rematch_started` handler; we don't want to blow it away.
      setRematch((prev) => ({
        status: 'none',
        isRequester: false,
        opponentIsRequester: false,
        expiresAt: null,
        requestedByName: null,
        previousMatchId: null,
        rematchChainId: prev?.rematchChainId ?? null,
        rematchIndex: Number(prev?.rematchIndex ?? 0),
      }));
      // Phase 3.11.1 hotfix — also fully flush terminal-reveal gate
      // state so the *new* match's terminal transition arms fresh.
      lastEventsTickRef.current = -1;
      terminalGateRef.current.lastShotCount = 0;
      if (terminalGateRef.current.fallbackTimeout) {
        clearTimeout(terminalGateRef.current.fallbackTimeout);
        terminalGateRef.current.fallbackTimeout = null;
      }
      setPendingTerminalPenaltyReveal(false);
      setTerminalGraceUntil(0);
      setPenaltyRevealUntil(0);
      setJokerSeq(null);
      setShotSeq(null);
      setDcSeq(null);
      setDiscardFlash(null);
      setAttackReveal(null);
      setHoldUntil(0);
      setH2hSummary(null);
      setH2hSummaryUpdating(false);
    }
    prevEngineMatchIdRef.current = currentEngineMatchId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEngineMatchId]);

  // Handle inbound rematch messages from the server. Also resets local
  // terminal-summary bookkeeping when a fresh match starts.
  function handleRematchMessage(msg) {
    if (!msg || !msg.type) return;
    if (msg.matchType) setMatchType((mt) => mt || msg.matchType);
    if (msg.type === 'rematch_error') {
      // Silently record; the button UI will already be in the right state.
      return;
    }
    if (msg.type === 'rematch_started') {
      // Reset ALL terminal-summary bookkeeping so the incoming fresh
      // state renders the new match cleanly. Every animation seq +
      // gate flag must go back to a pristine idle-match state.
      lastEventsTickRef.current = -1;
      terminalGateRef.current.lastShotCount = 0;
      if (terminalGateRef.current.fallbackTimeout) {
        clearTimeout(terminalGateRef.current.fallbackTimeout);
        terminalGateRef.current.fallbackTimeout = null;
      }
      setPendingTerminalPenaltyReveal(false);
      setTerminalGraceUntil(0);
      setPenaltyRevealUntil(0);
      setJokerSeq(null);
      setShotSeq(null);
      setDcSeq(null);
      setDiscardFlash(null);
      setAttackReveal(null);
      setHoldUntil(0);
      setH2hSummary(null);
      setH2hSummaryUpdating(false);
      setPreMatchRevealUntil(0);
      // Phase 3.11.1 — RESET rematch to fully-idle ('none') so the next
      // match end starts with a clean slate. The state broadcast that
      // follows this message flips the phase into the fresh match.
      setRematch({
        status: 'none',
        isRequester: false,
        opponentIsRequester: false,
        expiresAt: null,
        requestedByName: null,
        previousMatchId: null,
        // Carry forward chain metadata — the FRESH match is the next
        // link in the same chain, so the frontend cap check for Quick
        // needs to see the new `rematchIndex` to hide the button once
        // the cap is reached.
        rematchChainId: msg.rematchChainId || null,
        rematchIndex: Number(msg.rematchIndex ?? 0),
      });
      return;
    }
    // Phase 3.11.1 — SCOPE guard: reject any rematch coordination event
    // whose `previousMatchId` no longer matches the engine matchId we're
    // showing summary for. This prevents stale/queued events from an
    // old match from painting a phantom "Waiting" state.
    const currentMid = state?.matchId || null;
    if (msg.previousMatchId && currentMid && msg.previousMatchId !== currentMid) {
      // eslint-disable-next-line no-console
      console.log('[rematch] IGNORED stale event', {
        type: msg.type, msgPrev: msg.previousMatchId, currentMid,
      });
      return;
    }
    // Common accessor: is *this* player the requester?
    const iAmRequester = msg.requestedByPlayerId === playerId;
    const oppIsRequester = !!msg.requestedByPlayerId && msg.requestedByPlayerId !== playerId;

    if (msg.type === 'rematch_requested') {
      setRematch({
        status: 'requested',
        isRequester: iAmRequester,
        opponentIsRequester: oppIsRequester,
        expiresAt: msg.expiresAt || null,
        requestedByName: msg.requestedByName || null,
        previousMatchId: msg.previousMatchId || null,
        rematchChainId: msg.rematchChainId || null,
        rematchIndex: Number(msg.rematchIndex ?? 0),
      });
      return;
    }
    if (msg.type === 'rematch_accepted') {
      const bothAccepted = Array.isArray(msg.acceptedByPlayerIds) &&
        msg.acceptedByPlayerIds.includes('p1') &&
        msg.acceptedByPlayerIds.includes('p2');
      setRematch((prev) => ({
        ...prev,
        status: bothAccepted ? 'starting' : 'requested',
        isRequester: iAmRequester,
        opponentIsRequester: oppIsRequester,
        expiresAt: msg.expiresAt || prev.expiresAt,
        previousMatchId: msg.previousMatchId || prev.previousMatchId,
      }));
      return;
    }
    if (msg.type === 'rematch_declined') {
      setRematch((prev) => ({
        ...prev,
        status: 'declined',
        expiresAt: null,
      }));
      return;
    }
    if (msg.type === 'rematch_expired') {
      setRematch((prev) => ({
        ...prev,
        status: 'expired',
        expiresAt: null,
      }));
    }
  }

  const sendRematch = (kind) => {
    // PHASE 4.3.2B — direct rematch is allowed on Friend AND Quick
    // (but the SERVER enforces Quick's max-2 chain cap; a crafted
    // client-side request past the cap is rejected). Ranked never
    // allows direct rematch; the button is not rendered for that mode.
    if (matchType && matchType !== 'friend' && matchType !== 'quick_casual') return;
    // Phase 3.11.1 — tag the action with the engine matchId of the
    // currently-completed match so the server can reject stale actions.
    const previousMatchId = state?.matchId || null;
    socketRef.current?.sendRematch(kind, previousMatchId ? { previousMatchId } : null);
  };
  // PHASE 4.3.2B — helper for the summary UI to decide whether to show
  // the Rematch button. Mirrors the server's `directRematchMaxIndex`
  // rule so the button hides gracefully once Quick's cap is reached.
  // Friend has no cap (`null`).
  const canDirectRematch = (() => {
    if (matchType === 'friend') return true;
    if (matchType === 'quick_casual') {
      // After Quick's cap the server rejects further requests. Hide
      // the button so users see Queue Again instead. Chain caps to 2
      // rematches on top of the original, so the user can request
      // rematch while `rematchIndex < 2`.
      return (rematch?.rematchIndex ?? 0) < 2;
    }
    return false;   // ranked / bot / unknown
  })();
  const onRematchRequest = () => sendRematch('rematch_request');
  const onRematchAccept = () => sendRematch('rematch_accept');
  const onRematchDecline = () => sendRematch('rematch_decline');

  const dispatch = (action) => {
    // eslint-disable-next-line no-console
    console.log('[OnlineGame] dispatch', { tick: serverTick, action });
    socketRef.current?.send(action);
  };

  // Click handlers — branch on phase so Double Chance dispatches the right
  // engine action (matches local GameScreen line 430-450 verbatim).
  const onPlaceSlot = ({ slotType, slotIndex }) => {
    if (!state) return;
    const myTurnNow = state.currentPlayer === playerId;
    if (!myTurnNow) return;
    if (state.phase === 'joker-double-action') {
      dispatch({ type: 'JOKER_DOUBLE_PLACE', slotType, slotIndex });
      return;
    }
    dispatch({ type: 'PLACE', slotType, slotIndex });
  };
  const onAttackTarget = (target) => {
    if (!state) return;
    const myTurnNow = state.currentPlayer === playerId;
    if (!myTurnNow) return;
    if (state.phase === 'joker-double-action') {
      // DC attack — opponent atk/def only, never GK.
      if (target.slotType !== 'attack' && target.slotType !== 'defense') return;
      dispatch({ type: 'JOKER_DOUBLE_ATTACK', target });
      return;
    }
    dispatch({ type: 'ATTACK', target });
  };

  // ---- No-valid-move auto-pass (mirrors local GameScreen line 190-219) ----
  // If the current player has drawn a card with no legal placement and no
  // legal attack target, show "NO VALID MOVE" briefly then auto-send
  // END_TURN. The server validates and applies it; both clients see the
  // turn pass via the regular state broadcast.
  const isMeStuck =
    !!state &&
    state.phase === 'drawn' &&
    state.currentPlayer === playerId &&
    !!state.drawnCard &&
    !state.drawnCard.hidden && // never trigger on a masked opponent draw
    !hasAnyValidAction(state);

  const noMoveDispatchedRef = useRef(false);
  useEffect(() => {
    if (!isMeStuck) {
      noMoveDispatchedRef.current = false;
      return;
    }
    if (noMoveDispatchedRef.current) return;
    const t = setTimeout(() => {
      if (noMoveDispatchedRef.current) return;
      noMoveDispatchedRef.current = true;
      dispatch({ type: 'END_TURN' });
    }, NO_MOVE_VISIBLE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeStuck, state?.drawnCard?.id]);

  // Waiting-room ONLY before we've ever received a state. After we have
  // state (i.e. the match is live), a transient disconnect must NOT throw
  // the player back to the lobby — the reconnect overlay covers the board.
  if (!state) {
    const shareUrl = `${window.location.origin}/online?room=${roomCode}`;
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(roomCode);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[lobby] copy failed', e);
      }
    };
    const handleShare = async () => {
      const text = `Join my Kickard match!\nRoom code: ${roomCode}\n${shareUrl}`;
      try {
        if (navigator.share) {
          await navigator.share({ title: 'Kickard', text, url: shareUrl });
        } else {
          await navigator.clipboard.writeText(text);
        }
      } catch (e) {
        // user dismissed — silent.
      }
    };
    return (
      <div data-testid="online-waiting" className="kickard-frame stadium-bg flex flex-col items-center justify-center px-6 relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0" aria-hidden style={{
          background: 'radial-gradient(ellipse at 50% 18%, hsl(46 95% 60% / 0.16) 0%, transparent 55%)',
        }} />
        <div className="relative z-10 w-full max-w-sm flex flex-col items-center">
          <div className="font-heading text-3xl text-[hsl(var(--k-gold-bright))]" style={{ letterSpacing: '0.24em' }}>ONLINE</div>
          <div className="font-body text-[10px] text-white/45 uppercase tracking-[0.34em] mt-1 mb-8">Friend Match</div>

          <div className="text-white/55 text-[10px] tracking-[0.3em] uppercase mb-2">Room Code</div>
          <div
            data-testid="room-code"
            className="font-heading text-6xl text-white mb-1"
            style={{ letterSpacing: '0.18em', textShadow: '0 0 24px hsl(46 95% 60% / 0.35)' }}
          >
            {roomCode}
          </div>
          <div className="w-16 h-px bg-[hsl(var(--k-gold-bright)/0.5)] mb-6" />

          <div className="w-full flex gap-3 mb-8">
            <button
              data-testid="waiting-copy-btn"
              onClick={handleCopy}
              className="btn-secondary flex-1"
            >
              COPY
            </button>
            <button
              data-testid="waiting-share-btn"
              onClick={handleShare}
              className="btn-secondary flex-1"
            >
              SHARE
            </button>
          </div>

          <motion.div
            animate={{ opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
            className="text-white/70 text-center text-xs uppercase tracking-[0.3em]"
            data-testid="online-waiting-status"
          >
            {!connected
              ? 'Connecting…'
              : opponentConnected
                ? 'Starting match…'
                : 'Waiting for opponent…'}
          </motion.div>

          {/* Phase 3.5 — Head-to-head rivalry summary against this room's
              opponent. P0 hotfix: render ONLY after the opponent has
              actually connected AND the real H2H payload has landed, so
              we never flash a misleading "First meeting" line during the
              empty waiting state. */}
          {opponentConnected && h2h && (
            <motion.div
              key="h2h-waiting"
              initial={{ opacity: 0, y: 6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="w-full max-w-[320px] mt-6"
              data-testid="h2h-prematch-waiting"
            >
              <H2HCard h2h={h2h} myName={session.name} opponentName={h2h.opponentName} />
            </motion.div>
          )}

          {error && <div className="text-red-300 text-sm mt-4 px-2 text-center" data-testid="online-error">{error}</div>}
          <button onClick={handleLeave} className="text-white/50 text-[10px] tracking-[0.3em] uppercase mt-10" data-testid="online-leave-btn">LEAVE</button>
        </div>
      </div>
    );
  }

  // ---- Derived per-render -------------------------------------------------
  const isMyTurn = state.currentPlayer === playerId;
  const drawnIsMine = state.lastDrawnBy === playerId;
  const drawnExists = !!state.drawnCard;
  const myPlayer = state.players?.[playerId] || {};
  const oppPlayer = state.players?.[other] || {};
  const deckRemaining = state.deckRemaining ?? 0;
  const discardCount = Array.isArray(state.discard) ? state.discard.length : 0;
  const half = state.half || 1;

  const attackRevealActive = attackReveal !== null && Date.now() < attackReveal.expiresAt;
  const pendingReveal = jokerSeq !== null || shotSeq !== null || dcSeq !== null || attackRevealActive;

  // ---- Match-end summary --------------------------------------------------
  const pendingPenaltyReveal = Date.now() < penaltyRevealUntil;
  const terminalGraceActive = Date.now() < terminalGraceUntil;
  // Phase 3.8.2.3 — Auto-disarm the terminal-penalty gate once the
  // penalty reveal hold has expired. The event has already fired
  // (penaltyRevealUntil > 0), and the reveal frame has been on-screen
  // for PENALTY_REVEAL_HOLD_MS. Now the summary can safely render.
  if (pendingTerminalPenaltyReveal && penaltyRevealUntil > 0 && !pendingPenaltyReveal) {
    // Deferred so we don't setState during render.
    setTimeout(() => disarmTerminalPenaltyGate(), 0);
  }
  if (
    (state.phase === 'match-end' || state.phase === 'finished') &&
    state.endState &&
    !pendingReveal &&
    !pendingPenaltyReveal &&
    !terminalGraceActive &&
    !pendingTerminalPenaltyReveal &&
    Date.now() >= holdUntil
  ) {
    // Match concluded — don't auto-reattach next time the user opens /online.
    clearOnlineSession();
    return (
      <MatchSummaryView
        state={state}
        isOnline
        myPlayerId={playerId}
        opponentName={opponentName || state.players[other]?.name}
        h2h={h2hSummary || h2h}
        h2hUpdating={h2hSummaryUpdating && !h2hSummary}
        myName={session.name}
        matchType={matchType}
        myGuestId={loadGuest()?.guestId || null}
        rematch={rematch}
        canDirectRematch={canDirectRematch}
        onRematchRequest={onRematchRequest}
        onRematchAccept={onRematchAccept}
        onRematchDecline={onRematchDecline}
        onRematch={handleLeave}
        onHome={handleLeave}
      />
    );
  }

  // ---- Penalty shootout — rendered after extra time tied 2-2 ------------
  //
  // Phase 3.11.2 hotfix — the engine sets state.phase = 'finished' on the
  // SAME action that pushes the final PENALTY_REVEAL event to state.penalty.shots.
  // Without this extension the terminal-reveal-gate window (2.4 s hold)
  // would fall through to the normal game-board view — meaning the
  // decisive penalty card-pair reveal was invisible on both clients
  // (root cause of Bug A). Keep PenaltyView on-screen while the gate is
  // still holding, so the final `lastShot = shots[-1]` frame is visible.
  const hasPenaltyState = !!state.penalty && Array.isArray(state.penalty.shots) &&
                          state.penalty.shots.length > 0;
  const isTerminalPhase = state.phase === 'finished' || state.phase === 'match-end';
  const inTerminalPenaltyReveal = isTerminalPhase && hasPenaltyState &&
    (pendingTerminalPenaltyReveal || pendingPenaltyReveal || terminalGraceActive);
  // PHASE 5.0B-4.4 P0 — Presentation-order invariant.
  // A regulation reveal (Joker / DC / Shot / attack reveal) queued on
  // the LAST regulation action MUST render at its correct
  // chronological moment — AFTER the action resolves and BEFORE
  // PenaltyView takes visual priority. Without this gate the reveal
  // state (jokerSeq / shotSeq / dcSeq / attackRevealActive) survives
  // the switch to PenaltyView, sits alive through the entire penalty
  // shootout, and then renders after PenaltyView unmounts — visually
  // out of order. Holding PenaltyView until the pending reveal
  // clears preserves both the reveal AND chronology; the reveal
  // overlays a full-viewport modal so the underlying (now
  // penalty-phase) board is not visible. Engine / server / rules
  // are UNCHANGED — the authoritative phase already flipped to
  // `penalty`; only the frontend presentation is deferred.
  if ((state.phase === 'penalty' || inTerminalPenaltyReveal) && !pendingReveal) {
    return (
      <PenaltyView
        state={state}
        dispatch={dispatch}
        myPlayerId={playerId}
        opponentName={opponentName || oppPlayer.name || 'Opponent'}
        matchFinished={inTerminalPenaltyReveal}
        timer={timer}
        serverClockSkew={serverClockSkew}
        penaltyRevealUntil={penaltyRevealUntil}
      />
    );
  }

  // ---- BOARD VIEW (mirrors local GameScreen) -----------------------------
  const timerDisplay = deriveOnlineTimerDisplay({
    timer,
    serverClockSkew,
    isMyTurn,
    opponentName,
    playerId,
  });
  return (
    <div data-testid={TID.gameScreen} className="kickard-frame is-match pitch-bg kk-gameplay-root flex flex-col h-[100dvh] overflow-hidden" style={{ height: '100svh' }}>
      {/* TOP HUD — broadcast scoreboard (Phase 3B). All legacy testids preserved. */}
      <Scoreboard
        leftName={`YOU · ${session.name || myPlayer.name || (playerId === 'p1' ? 'Player 1' : 'Player 2')}`}
        rightName={opponentName || oppPlayer.name || 'Opponent'}
        leftNameTestId="online-me-name"
        rightNameTestId="online-opp-name"
        leftScore={playerId === 'p1' ? displayScores.p1 : displayScores.p2}
        rightScore={playerId === 'p1' ? displayScores.p2 : displayScores.p1}
        leftScoreTestId={TID.scoreP1}
        rightScoreTestId={TID.scoreP2}
        activeSide={isMyTurn ? 'left' : 'right'}
        halfLabel={half === 1 ? '1ST HALF' : '2ND HALF'}
        halfTestId="half-label"
        deckCount={deckRemaining}
        deckTotal={42}
        deckTestId={TID.deckCount}
        timerLabel={timerDisplay.label}
        timerTone={timerDisplay.tone}
        timerWrapperTestId="online-timer-hud"
        timerTestId="timer-countdown"
        strikesLabel={timerDisplay.strikesLabel}
        strikesTestId="strike-counter"
      />

      {/* BOARD — fills available height; central action hub overlays midline */}
      <div className="flex-1 flex items-center justify-center overflow-hidden relative">
        <PitchBackground />
        <GameBoard
          state={state}
          viewer={playerId}
          onPlaceSlot={onPlaceSlot}
          onAttackTarget={onAttackTarget}
          size="compact"
        />

        {/* CENTRAL ACTION HUB — Draw / drawn card / joker buttons */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center justify-center gap-1 pointer-events-none" style={{ minHeight: 56 }}>
          {/* DRAW ball — Phase 5.0B FINAL. Uses the EXISTING online
              render guard verbatim and the EXISTING dispatch. No new
              draw-eligibility rule and no duplicated handler. */}
          {isMyTurn && state.phase === 'turn-start' && !pendingReveal && (
            <motion.div
              key="draw"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="pointer-events-auto"
            >
              <MidfieldDrawBall
                onClick={() => dispatch({ type: 'DRAW' })}
                testId={TID.drawBtn}
                ariaLabel="Draw card"
              />
            </motion.div>
          )}

          {/* Opponent attack reveal — brief face-up flash of the attacking
              card on a normal (non-shot, non-joker) attack. Mirrors the
              local-mode behaviour where the bot's drawn card was always
              visible. Renders after state.drawnCard has been consumed. */}
          {attackReveal && Date.now() < attackReveal.expiresAt && !drawnExists && (
            <motion.div
              key={`attack-reveal-${attackReveal.attacker?.id}`}
              initial={{ scale: 0.65, opacity: 0, y: -8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="flex flex-col items-center gap-1"
              data-testid="opponent-attack-reveal"
            >
              <div className="font-heading text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--k-red))]">
                {(opponentName || 'Opponent')} attacked with
              </div>
              <div style={{ width: 'var(--kk-card-w)' }}>
                <PlayingCard card={attackReveal.attacker} highlight small />
              </div>
            </motion.div>
          )}

          {/* Drawn card — own card face-up, opponent face-down */}
          {drawnExists && (
            <motion.div
              key="drawn"
              initial={{ scale: 0.6, opacity: 0, y: -10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex flex-col items-center gap-1"
            >
              <div className="font-heading text-[10px] tracking-[0.3em] text-white/55 uppercase">
                {drawnIsMine ? 'Your draw' : `${opponentName || 'Opponent'} drew`}
              </div>
              <div data-testid={TID.drawnCard} style={{ width: 'var(--kk-card-w)' }}>
                <PlayingCard
                  card={state.drawnCard}
                  highlight
                  small
                  faceDown={!drawnIsMine || !!state.drawnCard?.hidden}
                />
              </div>
            </motion.div>
          )}
          {/* Phase 3.8.2.5 — Inline no-valid-move discard flash. Absolutely
              positioned ABOVE the DRAW button anchor so the DRAW button
              never shifts / overlaps regardless of whether it's currently
              rendered. `bottom: 100%` pins the flash card just above the
              hub, leaving the DRAW button pinned to the midline. */}
          {!drawnExists && discardFlash && (
            <motion.div
              key={`dsc-flash-${discardFlash.id}`}
              data-testid="discard-flash-inline"
              initial={{ scale: 0.6, opacity: 0, y: -6 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
              style={{ bottom: 'calc(100% + 8px)' }}
            >
              <div className="font-heading text-[10px] tracking-[0.3em] text-white/55 uppercase">
                {discardFlash.byMe
                  ? 'You drew — no move'
                  : `${opponentName || 'Opponent'} drew — no move`}
              </div>
              <div style={{ width: 'var(--kk-card-w)' }} data-testid="discard-flash-card">
                <PlayingCard card={discardFlash.card} small />
              </div>
            </motion.div>
          )}

          {/* Joker SHOOT / DOUBLE — only when it's MY joker-choice */}
          {state.phase === 'joker-choice' && isMyTurn && !pendingReveal && (
            <motion.div
              key="joker-choice"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="flex flex-col items-stretch gap-2 mt-2 pointer-events-auto"
              data-testid="joker-choice-panel"
            >
              <button
                type="button"
                data-testid="joker-shoot-btn"
                onClick={() => dispatch({ type: 'JOKER_SHOOT' })}
                className="btn-primary !text-sm !py-2 !px-5"
                style={{ minWidth: 180 }}
              >
                <span style={{ marginRight: 6 }}>⚽</span>SHOOT
              </button>
              <button
                type="button"
                data-testid="joker-double-btn"
                onClick={() => dispatch({ type: 'JOKER_DOUBLE_CHOOSE' })}
                className="btn-secondary !text-sm !py-2 !px-5"
                style={{ minWidth: 180 }}
              >
                <span style={{ marginRight: 6 }}>🎲</span>DOUBLE CHANCE
              </button>
            </motion.div>
          )}

          {/* Joker Double-Chance follow-up hint */}
          {state.phase === 'joker-double-action' && isMyTurn && !pendingReveal && (
            <motion.div
              key="joker-dc-hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
              className="font-heading text-[10px] tracking-[0.22em] text-[hsl(var(--k-gold-bright))] mt-1 px-3 py-1 bg-black/55 rounded uppercase"
              data-testid="joker-dc-hint"
            >
              DOUBLE CHANCE — attack or place
            </motion.div>
          )}

          {/* Opponent thinking label — opponent's turn, no drawn card yet */}
          {!isMyTurn && state.phase === 'turn-start' && !pendingReveal && (
            <div className="font-heading text-base tracking-[0.25em] text-white/65 px-3 py-1.5 bg-black/60 rounded">
              {(opponentName || 'OPPONENT').toUpperCase()}…
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM STATUS — Phase 5.0B FINAL kk-match-bottom shell. */}
      <div className="px-2 pt-1 pb-2 shrink-0">
        <MatchStatusBar
          turnLabel={isMyTurn ? (drawnExists ? 'PICK AN ACTION' : 'YOUR TURN') : `${(opponentName || 'OPPONENT').toUpperCase()} TURN`}
          turnMuted={!isMyTurn}
          turnTestId={TID.turnIndicator}
          helperText={
            !opponentConnected
              ? 'Opponent disconnected — waiting…'
              : error
                ? error
                : (isMyTurn && !drawnExists ? 'Select a card or draw' : null)
          }
          discardCount={discardCount}
          onExit={handleLeave}
          exitTestId="online-leave-mini"
          showExit={true}
        />
        {/* Retain hidden testids for existing state QA */}
        {!opponentConnected && (
          <span data-testid="online-opp-disconnected" className="sr-only">
            Opponent disconnected
          </span>
        )}
        {error && (
          <span data-testid="online-game-error" className="sr-only">
            {error}
          </span>
        )}
      </div>

      {/* Reveal sequences — same components as local GameScreen */}
      <AnimatePresence mode="wait">
        {shotSeq && <ShotSequence key="shot" sequence={shotSeq} onDone={() => setShotSeq(null)} />}
        {jokerSeq && <JokerSequence key="joker" sequence={jokerSeq} onDone={() => setJokerSeq(null)} />}
        {dcSeq && <DoubleChanceSequence key="dc" sequence={dcSeq} onDone={() => setDcSeq(null)} />}
      </AnimatePresence>

      {/* No-valid-move label — mirrors local GameScreen */}
      <AnimatePresence>
        {isMeStuck && (
          <motion.div
            key="no-move"
            data-testid="no-valid-move-label"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 -translate-x-1/2 z-[15] pointer-events-none"
            style={{ top: 'calc(50% + 76px)' }}
          >
            <div className="font-heading text-xs tracking-[0.28em] text-[hsl(var(--k-red))] bg-black/70 border border-[hsl(var(--k-red)/0.55)] px-3 py-1 rounded uppercase">
              No valid move
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mid-match reconnect overlay — non-destructive: the board remains
          mounted underneath, so on `onReconnected` the player resumes
          exactly where they left off. */}
      <AnimatePresence>
        {(reconnecting || reconnectFailed) && (
          <motion.div
            key="reconnect"
            data-testid="reconnecting-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-0 z-[40] flex items-center justify-center bg-black/72 backdrop-blur-sm"
          >
            <div className="flex flex-col items-center gap-4 px-6 py-5 bg-[hsl(var(--k-bg))]/95 border border-[hsl(var(--k-gold)/0.45)] rounded-lg">
              {!reconnectFailed ? (
                <>
                  <div className="font-heading text-base tracking-[0.24em] text-[hsl(var(--k-gold-bright))]">
                    RECONNECTING…
                  </div>
                  <div
                    className="font-mono text-xs text-white/70"
                    data-testid="reconnecting-countdown"
                  >
                    {Math.max(0, Math.ceil((reconnectInfo.totalMs - reconnectInfo.elapsedMs) / 1000))} s remaining
                    {reconnectInfo.attempt ? ` · attempt ${reconnectInfo.attempt}` : ''}
                  </div>
                  <div className="text-white/55 text-[10px] text-center max-w-[240px]">
                    Hold on — we&apos;re re-attaching you to the same match.
                  </div>
                  {/* Phase 3.8.2 — manual retry escape for iPhone/PWA where
                      background suspension may freeze the countdown. */}
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        // PHASE 3.13.2 hotfix — if the socket is already
                        // healthy and receiving live state (serverTick > 0
                        // was updated within the last ~4 s), the reconnect
                        // overlay is stale. Clear it instead of pointlessly
                        // asking the socket layer to "resume".
                        try {
                          const sock = socketRef.current;
                          const wsAlive = !!(sock && sock.ws
                            && sock.ws.readyState === WebSocket.OPEN);
                          const lastMsgAgeMs = sock
                            ? (Date.now() - (sock.lastServerMsgAt || 0))
                            : Infinity;
                          if (wsAlive && lastMsgAgeMs < 4000) {
                            setReconnecting(false);
                            setReconnectFailed(false);
                            setError(null);
                            return;
                          }
                          sock?.resume();
                        } catch (_e) { /* ignore */ }
                      }}
                      className="back-chip !text-[10px] !px-3 !py-1.5"
                      data-testid="reconnect-retry-now-btn"
                    >
                      RETRY NOW
                    </button>
                    <button
                      type="button"
                      onClick={handleLeave}
                      className="back-chip !text-[10px] !px-3 !py-1.5"
                      data-testid="reconnect-return-home-btn"
                    >
                      RETURN HOME
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="font-heading text-base tracking-[0.24em] text-[hsl(var(--k-red))]">
                    RECONNECT FAILED
                  </div>
                  <div className="text-white/65 text-xs text-center max-w-[240px]">
                    {error || 'Could not reconnect within 30 seconds.'}
                  </div>
                  <button
                    type="button"
                    onClick={handleLeave}
                    className="btn-secondary !text-xs !py-2 !px-4"
                    data-testid="reconnect-give-up-btn"
                  >
                    RETURN TO LOBBY
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* P0 hotfix — pre-match H2H lower-third. Non-blocking overlay
          rendered over the early board view so the rivalry record stays
          readable for PRE_MATCH_REVEAL_MS even after the server's first
          state message has flipped us out of the waiting room. Uses
          pointer-events-none so the DRAW button and timer remain fully
          interactive underneath — we do NOT consume the first-turn
          timer.

          Mobile-safe positioning: anchored by left:12px + right:12px so
          the panel naturally fits the viewport at 360 / 375 / 390 / 414
          / 430 px portrait widths without any horizontal clipping or
          page overflow. No fixed pixel width, no translateX. */}
      <AnimatePresence>
        {preMatchRevealActive && h2h && (
          <motion.div
            key="h2h-prematch-board"
            data-testid="h2h-prematch-lower-third"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="pointer-events-none absolute z-[30] flex justify-center"
            style={{
              bottom: 'calc(54px + env(safe-area-inset-bottom, 0px))',
              left: '12px',
              right: '12px',
              boxSizing: 'border-box',
            }}
          >
            <div
              className="rounded-xl shadow-[0_8px_28px_rgba(0,0,0,0.55)] backdrop-blur-sm"
              style={{ width: '100%', maxWidth: '420px', boxSizing: 'border-box' }}
            >
              <H2HCard h2h={h2h} myName={session.name} opponentName={h2h.opponentName} showLast={true} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Halftime overlay — both players see it; the first to confirm advances */}
      <AnimatePresence>
        {state.phase === 'halftime' && !pendingReveal && Date.now() >= holdUntil && (
          <HalftimeOverlay
            scoreP1={state.players.p1.score}
            scoreP2={state.players.p2.score}
            nextStarter={state.currentPlayer}
            onContinue={() => dispatch({ type: 'START_SECOND_HALF' })}
            isOnline
            myPlayerId={playerId}
            meName={session.name || myPlayer.name}
            opponentName={opponentName || oppPlayer.name}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


// =================== Server-driven timer HUD (Phase 2.4) ===================
/**
 * Pure helper — computes the timer label/tone/strikes display from the
 * server-driven `timer` payload + local clock skew. Returned shape:
 *   { label, tone, strikesLabel }
 * tone ∈ 'red' | 'amber' | 'gold' | 'green' | 'default'
 */
export function deriveOnlineTimerDisplay({ timer, serverClockSkew, isMyTurn, opponentName, playerId }) {
  if (!timer) return { label: '—', tone: 'default', strikesLabel: null };
  const serverNow = Date.now() + serverClockSkew;
  const cfg = timer.config || {};
  const phase = timer.phase;
  const strikes = timer.strikes || { p1: 0, p2: 0 };
  const myStrikes = strikes[playerId] || 0;

  let label = null;
  let remainingMs = 0;
  let tone = 'default';
  if (phase === 'no-valid-move') {
    label = 'NO VALID MOVE';
    tone = 'red';
  } else if (phase === 'time-bank') {
    remainingMs = Math.max(0, (timer.timeBankActiveUntil || 0) - serverNow);
    label = `BANK ${(remainingMs / 1000).toFixed(1)}`;
    tone = 'amber';
  } else if (phase === 'pre-draw' && !timer.autoDrawFired) {
    const a = Math.max(0, (timer.autoDrawDeadline || 0) - serverNow);
    label = `AUTO ${(a / 1000).toFixed(1)}`;
    tone = 'gold';
    remainingMs = a;
  } else {
    remainingMs = Math.max(0, (timer.turnDeadline || 0) - serverNow);
    label = `${(remainingMs / 1000).toFixed(1)}s`;
    tone = remainingMs < 5000 ? 'red' : 'gold';
  }
  if (phase === 'match-end' || timer.forfeitWinner) {
    label = timer.forfeitWinner === playerId ? 'WIN · OPP FORFEIT' : 'FORFEIT';
    tone = 'red';
  }

  // Side prefix is encoded in the broadcast active-side underline, no need
  // to repeat "YOU · " or "OPP · " in the label itself anymore.
  const sidePrefix = isMyTurn ? 'YOU' : (opponentName || 'OPP').toUpperCase();
  const fullLabel = `${sidePrefix} · ${label}`;

  const strikesLabel = myStrikes > 0
    ? `STR ${myStrikes}/${cfg.maxStrikes || 3}`
    : null;

  return { label: fullLabel, tone, strikesLabel };
}

